/**
 * MONSTER TOWN — FISH AND BUGS. DATA ONLY. Owned by the FISHING/BUGS crew.
 *
 * Nothing in this file is engine. A new creature is an entry in `CREATURES` and
 * that is the whole change — `registerContent()` already spreads this array into
 * `registerCollectibles()`, and the catching systems read the registry rather
 * than any list of their own. There is no species name anywhere in
 * `src/catching/**` or `src/ui/collection.ts`.
 *
 * ===========================================================================
 *  WHAT IS OUT DEPENDS ON WHEN AND WHERE. THAT IS THE LESSON.
 * ===========================================================================
 * Expansion brief §6: "morning butterflies, evening fireflies, night beetles,
 * pond fish vs river fish — that's real observation and categorisation." So the
 * `where` rules below are the actual content of this subsystem: a kid who wants
 * the firefly has to come back at dusk, and the way they take control of that is
 * the bed (§1). The day cycle pays off here or it pays off nowhere.
 *
 * TWO RULES THE TABLE MUST KEEP, AND HOW TO CHECK THEM:
 *
 *  1. NOTHING IS EVER UNFINDABLE (LAW #2, and `collection.ts`'s own header).
 *     At every hour of the day there is at least one bug out in the GRASS, one
 *     in the TREES and one fish in the POND. Walk the table hour by hour before
 *     you edit it; `npm run catch-check` asserts all 24 hours at all three
 *     places and fails on a single hole. A kid who goes fishing at 3am and is
 *     told "nothing is here" has been failed by a game that cannot fail them.
 *
 *  2. MORNING AND NIGHT MUST GENUINELY DIFFER, or the whole clock is decoration.
 *     `npm run day-check` compares `availableNow('bug','grass')` at 10:00 and at
 *     23:00 and requires two different sets.
 *
 * COVERAGE, hour by hour (the working, so the next person can check it):
 *   grass  00-04 firefly+moth · 04-05 snail+moth · 05-07 pinkfly+snail
 *          07-18 the day shift · 18-20 firefly · 20-24 firefly+moth
 *   tree   04-09 snail · 08-19 cicada · 19-05 beetle · 20-05 moth
 *   pond   the smiley fish has NO hour rule at all — it is the floor under
 *          every other line in this file, and it is why a 3am cast still works.
 *
 * `likes` is filled in for the ZOO crew, which reads the same registry: a
 * creature's food, habitat and sound are the zoo's whole learning payload and
 * they belong with the creature, not with the enclosure.
 */

import { defineCollectible, type CollectionEntry } from '../core/collection'
import type { PaletteName } from '../core/palette'
import {
  beetle,
  bee,
  butterfly,
  cicada,
  firefly,
  fish,
  grasshopper,
  ladybug,
  moth,
  snail,
  tadpole,
} from '../catching/art2d'
import type { Painter } from '../catching/paint'

// ---------------------------------------------------------------------------
// bugs
// ---------------------------------------------------------------------------

const GRASS = ['grass', 'garden'] as const
const TREES = ['tree'] as const

export const bugButterflyBlue = defineCollectible({
  id: 'bug_butterfly_blue',
  kind: 'bug',
  devName: 'Blue butterfly',
  icon: 'plant',
  draw: butterfly('monsterSky', 'wallBlue'),
  where: [{ places: GRASS, fromHour: 7, toHour: 17, rarity: 1 }],
  tags: ['flyer', 'day'],
  likes: { food: ['ing_flower'], habitat: ['garden'], sound: 'sparkle_soft' },
  coins: 6,
})

export const bugButterflyPink = defineCollectible({
  id: 'bug_butterfly_pink',
  kind: 'bug',
  devName: 'Pink butterfly',
  icon: 'plant',
  draw: butterfly('flowerPink', 'flowerWhite'),
  where: [{ places: GRASS, fromHour: 5, toHour: 14, rarity: 0.9 }],
  tags: ['flyer', 'morning'],
  likes: { food: ['ing_flower'], habitat: ['garden'], sound: 'sparkle_soft' },
  coins: 6,
})

export const bugLadybug = defineCollectible({
  id: 'bug_ladybug',
  kind: 'bug',
  devName: 'Ladybird',
  icon: 'plant',
  draw: ladybug,
  where: [{ places: GRASS, fromHour: 8, toHour: 18, rarity: 1 }],
  tags: ['crawler', 'day'],
  likes: { food: ['ing_leaf'], habitat: ['garden'], sound: 'pop_soft' },
  coins: 5,
})

export const bugBee = defineCollectible({
  id: 'bug_bee',
  kind: 'bug',
  devName: 'Bumble bee',
  icon: 'plant',
  draw: bee,
  where: [{ places: GRASS, fromHour: 8, toHour: 17, rarity: 0.8 }],
  tags: ['flyer', 'day'],
  likes: { food: ['ing_flower'], habitat: ['garden'], sound: 'sparkle_soft' },
  coins: 7,
})

export const bugGrasshopper = defineCollectible({
  id: 'bug_grasshopper',
  kind: 'bug',
  devName: 'Grasshopper',
  icon: 'plant',
  draw: grasshopper,
  where: [{ places: GRASS, fromHour: 9, toHour: 19, rarity: 0.7 }],
  tags: ['hopper', 'day'],
  likes: { food: ['ing_leaf'], habitat: ['grass'], sound: 'leaves_rustle' },
  coins: 8,
})

export const bugFirefly = defineCollectible({
  id: 'bug_firefly',
  kind: 'bug',
  devName: 'Firefly',
  icon: 'sparkle',
  draw: firefly,
  // 18:00 through 04:00 — one rule, wrapped through midnight.
  where: [{ places: GRASS, fromHour: 18, toHour: 4, rarity: 1 }],
  tags: ['flyer', 'night', 'glow'],
  likes: { food: ['ing_berry'], habitat: ['grass'], sound: 'sparkle_soft' },
  coins: 12,
})

export const bugMoth = defineCollectible({
  id: 'bug_moth',
  kind: 'bug',
  devName: 'Moon moth',
  icon: 'plant',
  draw: moth,
  where: [
    { places: GRASS, fromHour: 20, toHour: 5, rarity: 0.9 },
    { places: TREES, fromHour: 20, toHour: 5, rarity: 0.9 },
  ],
  tags: ['flyer', 'night'],
  likes: { food: ['ing_leaf'], habitat: ['tree'], sound: 'swish_soft' },
  coins: 10,
})

export const bugBeetle = defineCollectible({
  id: 'bug_beetle',
  kind: 'bug',
  devName: 'Stag beetle',
  icon: 'tree',
  draw: beetle,
  where: [{ places: TREES, fromHour: 19, toHour: 5, rarity: 0.6 }],
  tags: ['crawler', 'night'],
  likes: { food: ['ing_sap'], habitat: ['tree'], sound: 'wood_clack' },
  coins: 16,
})

export const bugSnail = defineCollectible({
  id: 'bug_snail',
  kind: 'bug',
  devName: 'Snail',
  icon: 'plant',
  draw: snail,
  where: [
    { places: GRASS, fromHour: 4, toHour: 9, rarity: 0.8 },
    { places: TREES, fromHour: 4, toHour: 9, rarity: 0.8 },
  ],
  tags: ['crawler', 'dawn'],
  likes: { food: ['ing_leaf'], habitat: ['garden'], sound: 'pop_soft' },
  coins: 6,
})

export const bugCicada = defineCollectible({
  id: 'bug_cicada',
  kind: 'bug',
  devName: 'Cicada',
  icon: 'tree',
  draw: cicada,
  where: [{ places: TREES, fromHour: 8, toHour: 19, rarity: 1 }],
  tags: ['flyer', 'day'],
  likes: { food: ['ing_sap'], habitat: ['tree'], sound: 'leaves_rustle' },
  coins: 9,
})

// ---------------------------------------------------------------------------
// fish
// ---------------------------------------------------------------------------

const POND = ['pond'] as const

/**
 * THE FLOOR UNDER THE WHOLE POND. No hour rule, no phase rule, no `afterDay`:
 * whatever else is or is not out, this one is. Do not add a time window to it
 * without adding another all-hours fish first.
 */
export const fishSmiley = defineCollectible({
  id: 'fish_smiley',
  kind: 'fish',
  devName: 'Smiley fish',
  icon: 'water',
  draw: fish({ body: 'uiAccent', fin: 'uiAccentDark' }),
  where: [{ places: POND, rarity: 1 }],
  tags: ['pond'],
  likes: { food: ['ing_crumb'], habitat: ['pond'], sound: 'water_plop' },
  coins: 6,
})

export const fishSunny = defineCollectible({
  id: 'fish_sunny',
  kind: 'fish',
  devName: 'Sunfish',
  icon: 'water',
  draw: fish({ body: 'flowerYellow', fin: 'roofMustard', marks: 'spots' }),
  where: [{ places: POND, fromHour: 7, toHour: 17, rarity: 0.9 }],
  tags: ['pond', 'day'],
  likes: { food: ['ing_crumb'], habitat: ['pond'], sound: 'water_plop' },
  coins: 9,
})

export const fishStripe = defineCollectible({
  id: 'fish_stripe',
  kind: 'fish',
  devName: 'Stripey fish',
  icon: 'water',
  draw: fish({ body: 'flowerRed', fin: 'iceChocolate', marks: 'stripes' }),
  where: [{ places: POND, fromHour: 5, toHour: 12, rarity: 0.8 }],
  tags: ['pond', 'morning'],
  likes: { food: ['ing_crumb'], habitat: ['pond'], sound: 'water_plop' },
  coins: 10,
})

export const fishTadpole = defineCollectible({
  id: 'fish_tadpole',
  kind: 'fish',
  devName: 'Tadpole',
  icon: 'water',
  draw: tadpole,
  where: [{ places: POND, fromHour: 6, toHour: 16, rarity: 1 }],
  tags: ['pond', 'day', 'small'],
  likes: { food: ['ing_leaf'], habitat: ['pond'], sound: 'water_plop' },
  coins: 4,
})

export const fishStar = defineCollectible({
  id: 'fish_star',
  kind: 'fish',
  devName: 'Star fish',
  icon: 'sparkle',
  draw: fish({ body: 'monsterLilac', fin: 'flowerPurple', badge: 'star' }),
  where: [{ places: POND, fromHour: 17, toHour: 20, rarity: 0.7 }],
  tags: ['pond', 'evening'],
  likes: { food: ['ing_berry'], habitat: ['pond'], sound: 'sparkle_soft' },
  coins: 14,
})

export const fishMoon = defineCollectible({
  id: 'fish_moon',
  kind: 'fish',
  devName: 'Moonfish',
  icon: 'sparkle',
  draw: fish({ body: 'wallBlue', fin: 'monsterSky', badge: 'moon' }),
  where: [{ places: POND, fromHour: 20, toHour: 5, rarity: 0.9 }],
  tags: ['pond', 'night'],
  likes: { food: ['ing_berry'], habitat: ['pond'], sound: 'sparkle_soft' },
  coins: 16,
})

export const fishWhiskers = defineCollectible({
  id: 'fish_whiskers',
  kind: 'fish',
  devName: 'Whiskers',
  icon: 'water',
  draw: fish({ body: 'trunk', fin: 'trunkDark', whiskers: true }),
  where: [{ places: POND, fromHour: 18, toHour: 6, rarity: 0.6 }],
  tags: ['pond', 'night', 'big'],
  likes: { food: ['ing_crumb'], habitat: ['pond'], sound: 'water_plop' },
  coins: 15,
})

/**
 * The rare one. `rarity` is low and `afterDay` is late, and BOTH are gentle:
 * rarity is floored at 0.05 by the registry so it can always turn up, and
 * `afterDay` is pacing, never a lock — every other fish is out meanwhile.
 */
export const fishRainbow = defineCollectible({
  id: 'fish_rainbow',
  kind: 'fish',
  devName: 'Rainbow fish',
  icon: 'star',
  draw: fish({ body: 'flowerWhite', fin: 'uiAccent', marks: 'rainbow' }),
  where: [{ places: POND, fromHour: 9, toHour: 16, afterDay: 3, rarity: 0.2 }],
  tags: ['pond', 'rare'],
  likes: { food: ['ing_berry'], habitat: ['pond'], sound: 'chime_happy' },
  coins: 30,
})

// ---------------------------------------------------------------------------
// how each one looks in the WORLD (the 3D actor)
// ---------------------------------------------------------------------------

/** How a creature moves when it is out in the town. Art, not behaviour rules. */
export type CreatureMotion = 'flutter' | 'crawl' | 'hop' | 'glow'

export interface CreatureLook {
  readonly body: PaletteName
  readonly accent: PaletteName
  readonly motion: CreatureMotion
  /** Sits up in the leaves rather than down in the grass. */
  readonly high?: boolean
  /**
   * THE BOOK PICTURE IS A PLAN VIEW, so the world may wear it.
   *
   * A `crawl` creature with this set gets `CollectionEntry.draw` baked into a
   * cached texture and laid face-up on a low dome, instead of the featureless
   * shell the round-`vr1` critic caught ("an unreadable red lozenge; the book
   * promises a beautiful beetle"). It is opt-in per creature because it is a
   * claim about the ART, not about the animal: a ladybird and a stag beetle are
   * drawn from above and read perfectly lying on the grass; a snail is drawn
   * from the side and would read as a snail that has fallen over.
   *
   * It costs nothing to leave off. A creature without it keeps the 3D shell.
   */
  readonly topDown?: boolean
  /**
   * IT COMES OUT AFTER DARK, SO IT CARRIES ITS OWN LIGHT.
   *
   * Round `vr2`: "a night bug is a pale grey moth on dark grass... at plaza
   * distance it is almost invisible, which undercuts the point of night
   * creatures being worth going out for." The night wash is a MULTIPLY pane over
   * the finished picture, so it takes a creature's contrast down with the
   * grass's — see the long note on `lantern` in `src/catching/art.ts`, which
   * explains why the answer is an additive glow in the lamp-pool render band and
   * not a brighter body colour (a brighter body is multiplied down too).
   *
   * Set it on anything whose availability rule is mostly after dark. It is a
   * PALETTE NAME, so a new night creature is still one line of data.
   */
  readonly lantern?: PaletteName
}

/**
 * The 3D tint table, kept HERE beside the 2D painter so a bug in the grass and
 * its page in the book can never drift into being two different animals. A
 * creature with no row still spawns — `src/catching/art.ts` falls back — so a
 * forgotten line costs a colour, never a creature.
 */
export const CREATURE_LOOK: Readonly<Record<string, CreatureLook>> = {
  bug_butterfly_blue: { body: 'monsterSky', accent: 'wallBlue', motion: 'flutter' },
  bug_butterfly_pink: { body: 'flowerPink', accent: 'flowerWhite', motion: 'flutter' },
  bug_ladybug: { body: 'flowerRed', accent: 'ink', motion: 'crawl', topDown: true },
  bug_bee: { body: 'flowerYellow', accent: 'ink', motion: 'flutter' },
  bug_grasshopper: { body: 'monsterLime', accent: 'grassDark', motion: 'hop' },
  // The three that are only ever out after dark all carry a lantern. See the
  // field's own note: without it the night grade takes their contrast down with
  // the grass's, and a creature you cannot see is a creature you cannot catch.
  bug_firefly: { body: 'uiCoin', accent: 'flowerYellow', motion: 'glow', lantern: 'flowerYellow' },
  // Its wings stay `wallLilac` — the SAME colour the book's moth is painted in.
  // The header above is not decoration: a moth that is lilac on the page and
  // cream in the grass is two animals to a kid who cannot read either name. The
  // night reading is fixed with light, not by repainting one of the two.
  bug_moth: {
    body: 'wallLilac',
    accent: 'woodDark',
    motion: 'flutter',
    high: true,
    lantern: 'flowerWhite',
  },
  bug_beetle: {
    body: 'trunk',
    accent: 'trunkDark',
    motion: 'crawl',
    high: true,
    topDown: true,
    lantern: 'roofMustard',
  },
  bug_snail: { body: 'roofMustard', accent: 'monsterCream', motion: 'crawl' },
  bug_cicada: { body: 'grassDark', accent: 'glassDark', motion: 'flutter', high: true },
  fish_smiley: { body: 'uiAccent', accent: 'uiAccentDark', motion: 'crawl' },
  fish_sunny: { body: 'flowerYellow', accent: 'roofMustard', motion: 'crawl' },
  fish_stripe: { body: 'flowerRed', accent: 'iceChocolate', motion: 'crawl' },
  fish_tadpole: { body: 'leafDark', accent: 'grassDark', motion: 'crawl' },
  fish_star: { body: 'monsterLilac', accent: 'flowerPurple', motion: 'crawl' },
  fish_moon: { body: 'wallBlue', accent: 'monsterSky', motion: 'crawl' },
  fish_whiskers: { body: 'trunk', accent: 'trunkDark', motion: 'crawl' },
  fish_rainbow: { body: 'flowerWhite', accent: 'uiAccent', motion: 'crawl' },
}

/** The big picture for a book page / a catch card. Falls back to a friendly egg. */
export function creaturePainter(id: string): Painter | null {
  const e = CREATURES.find((x) => x.id === id)
  return e?.draw ?? null
}

/** Everything registered at boot. Add your creature to this array; that is all. */
export const CREATURES: readonly CollectionEntry[] = [
  // bugs
  bugButterflyBlue,
  bugButterflyPink,
  bugLadybug,
  bugBee,
  bugGrasshopper,
  bugFirefly,
  bugMoth,
  bugBeetle,
  bugSnail,
  bugCicada,
  // fish
  fishSmiley,
  fishSunny,
  fishStripe,
  fishTadpole,
  fishStar,
  fishMoon,
  fishWhiskers,
  fishRainbow,
]
